<template>
	<div>
		<tw-modal
			ref="payInvoiceModal"
			:title="`Pay Invoice #${invoice.invoiceNumber}`"
			modal-max-width="max-w-screen-md"
			:close-modal="cancelUnconfirmedPayment">
			<div>
				<div
					v-if="!stripeElementsLoaded"
					class="py-16">
					<tw-loading class="!h-8 !w-8" />
				</div>

				<!-- a visitor who started a microdeposit verification sees this instead of the form
					when they look the invoice up again -->
				<div
					v-if="stripeElementsLoaded && needsMicrodepositVerification"
					class="mt-6">
					<v-heading>
						Verify Your Bank Account
					</v-heading>

					<p class="mt-4 text-sm/6 dark:text-gray-300">
						We sent a small deposit to your bank account, which can take 1 to 2 business days to
						appear on your statement. Enter the deposit amount to finish paying this invoice.
					</p>

					<div
						v-if="message && !paymentUnavailable"
						class="mt-4 text-sm font-bold text-red-600 dark:text-red-500">
						{{ message }}
					</div>

					<div class="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
						<tw-button
							@click="switchPaymentMethod()"
							class="btn-base btn-white w-full sm:w-auto"
							:disabled="switchingPaymentMethod"
							:loading="switchingPaymentMethod">
							Use a Different Payment Method
						</tw-button>

						<tw-button
							@click="openVerificationWindow()"
							class="btn-base btn-indigo w-full sm:w-48"
							:disabled="switchingPaymentMethod || !verificationHostedUrl">
							Verify Deposit
						</tw-button>
					</div>
				</div>

				<div v-show="stripeElementsLoaded && !needsMicrodepositVerification">
					<p
						v-if="paymentUnavailable"
						class="mt-6 text-sm"
						:class="unavailable.isError
							? 'font-bold text-red-600 dark:text-red-500'
							: 'text-gray-600 dark:text-gray-300'">
						{{ unavailable.text }}
					</p>

					<div
						v-show="!paymentUnavailable"
						id="anonymous-payment-element"
						class="mt-6" />

					<div class="mt-6 border-t border-gray-200 pt-4 text-sm dark:border-white/10">
						<div
							v-for="(service, index) in invoice.services"
							:key="index + '-service'"
							class="flex justify-between pb-2">
							<span class="truncate pr-4 text-gray-500 dark:text-gray-400">
								{{ service.description }}
							</span>
							<span class="tabular-nums dark:text-gray-300">
								{{ currencyFormatter.format(service.total) }}
							</span>
						</div>

						<!-- same breakdown as the signed in pay form: total, processing fee, tax, paid -->
						<div class="flex justify-between border-t border-gray-200 pt-4 dark:border-white/10">
							<span class="text-gray-500 dark:text-gray-400">Total</span>
							<span class="font-bold dark:text-white">
								{{ currencyFormatter.format(servicesTotal) }}
							</span>
						</div>

						<div
							v-if="!paymentUnavailable && payingByCard && invoice.ccFee > 0"
							class="mt-2 flex justify-between">
							<span class="text-gray-500 dark:text-gray-400">Processing Fee</span>
							<span class="font-bold dark:text-white">
								{{ currencyFormatter.format(invoice.ccFee) }}
							</span>
						</div>

						<div
							v-if="tax > 0"
							class="mt-2 flex justify-between">
							<span class="text-gray-500 dark:text-gray-400">Tax</span>
							<span class="font-bold dark:text-white">
								{{ currencyFormatter.format(tax) }}
							</span>
						</div>

						<div
							v-if="amountPaid > 0"
							class="mt-2 flex justify-between">
							<span class="text-gray-500 dark:text-gray-400">Paid</span>
							<span class="font-bold dark:text-white">
								-{{ currencyFormatter.format(amountPaid) }}
							</span>
						</div>

						<div
							v-if="!paymentUnavailable"
							class="mt-4 flex justify-between border-t border-gray-200 pt-4 text-lg dark:border-white/10">
							<span class="font-bold dark:text-white">Amount Due</span>
							<span class="font-bold dark:text-white">
								{{ currencyFormatter.format(amountDueWithFee) }}
							</span>
						</div>
					</div>

					<div
						v-if="message && !paymentUnavailable"
						class="mt-4 text-sm font-bold text-red-600 dark:text-red-500">
						{{ message }}
					</div>

					<div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
						<tw-button
							@click="closeDialog()"
							class="btn-base btn-white w-full sm:w-auto"
							:disabled="submitting">
							{{ paymentUnavailable ? 'Close' : 'Cancel' }}
						</tw-button>

						<!-- nothing can be paid when the intent could not be created, so the amount
							would only ever read as zero here -->
						<tw-button
							v-if="!paymentUnavailable"
							@click="submitPayment()"
							class="btn-base btn-indigo w-full sm:w-48"
							:disabled="submitting"
							:loading="submitting">
							Pay {{ currencyFormatter.format(amountDueWithFee) }}
						</tw-button>
					</div>
				</div>

				<p
					v-if="stripeElementsLoaded"
					class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
					Questions? Contact us at
					<a
						href="mailto:partner_ops@dbservices.com"
						class="link">partner_ops@dbservices.com</a>
				</p>
			</div>
		</tw-modal>
	</div>
</template>

<script lang="ts">
	export default { name: 'PayInvoiceDialog' } // name the component
</script>

<script setup lang="ts">
	// api
	import {
		applyGuestConvenienceFee,
		cancelGuestPaymentIntent,
		createGuestPaymentIntent,
		guestPayErrorMessage
	} from '@/utilities/guestPayApi'

	// stripe
	import {
		loadStripe
	} from '@stripe/stripe-js/pure'

	// types
	import {
		Invoice
	} from '@/types'

	// utilities
	import {
		currencyFormatter
	} from '@/utilities/format'

	// vue
	import {
		computed,
		nextTick,
		onUnmounted,
		PropType,
		ref
	} from 'vue'

	const CARD_PAYMENT_METHOD = 'card'

	// invoice states that explain a refusal better than the Lambda's deliberately vague message
	const PAID_STATUS = 'Paid'
	const CANCELLED_STATUS = 'Cancelled'
	const PENDING_PAYMENT_STATUS = 'pending'

	const PAYMENT_ERROR_MESSAGE = "We couldn't start this payment. Please try again or contact us for assistance."

	const CANCEL_ERROR_MESSAGE = "We couldn't cancel that payment. Please try again or contact us for assistance."

	const VERIFICATION_WINDOW_WIDTH = 500
	const VERIFICATION_WINDOW_HEIGHT = 600

	// how often the verification window is checked for having been closed, since a window opened
	// with window.open raises no event when it goes away
	const VERIFICATION_POLL_MS = 1000

	const props = defineProps({
		paymentToken: {
			type: String,
			required: true
		},
		invoice: {
			type: Object as PropType<Invoice>,
			required: true
		}
	})

	const emit = defineEmits([
		'closed',
		'paid',
		'verification-canceled',
		'verification-completed',
		'verification-started'
	])

	const stripePromise = loadStripe(process.env.VUE_APP_STRIPE_API_KEY || "")

	const payInvoiceModal = ref<any>(null)
	const stripe = ref<any>(null)
	const elements = ref<any>(null)

	const clientSecret = ref("")
	const loading = ref(false)
	const message = ref("")
	const needsMicrodepositVerification = ref(false)
	const paidInStripe = ref(false)
	const paymentUnavailable = ref(false)
	const selectedPaymentMethod = ref("")
	const stripeElementsLoaded = ref(false)
	const submitting = ref(false)
	const switchingPaymentMethod = ref(false)
	const verificationHostedUrl = ref("")

	let verificationWatcher = 0

	// next_action is the authority rather than status, because Stripe clears next_action once the
	// intent is canceled or confirmed, and the status name for this state varies by API version
	function isAwaitingMicrodepositVerification(paymentIntent: any) {
		return paymentIntent?.next_action?.type === "verify_with_microdeposits"
	}

	// money is moving: settled for a card, still clearing for a bank payment
	function isPaymentUnderWay(paymentIntent: any) {
		return paymentIntent?.status === 'processing' || paymentIntent?.status === 'succeeded'
	}

	// what the page shows once a payment goes through. Stripe counts in cents, and its amount is what
	// was actually taken, card fee included
	function paymentConfirmation(paymentIntent: any) {
		return {
			invoiceNumber: props.invoice.invoiceNumber,
			amount: paymentIntent.amount / 100,
			settled: paymentIntent.status === 'succeeded'
		}
	}

	// parks the dialog on the verification panel, from which the visitor opens Stripe's hosted page
	function showVerificationPanel(paymentIntent: any) {
		verificationHostedUrl.value = paymentIntent.next_action.verify_with_microdeposits?.hosted_verification_url || ""
		needsMicrodepositVerification.value = true
	}

	// opens Stripe's hosted deposit verification page in a small centered window. going through with
	// it commits the visitor to ACH, so our dialog closes rather than sitting behind Stripe's still
	// offering a different payment method
	function openVerificationWindow() {
		if (!verificationHostedUrl.value) return

		const width = VERIFICATION_WINDOW_WIDTH
		const height = VERIFICATION_WINDOW_HEIGHT
		const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2)
		const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2)

		const verificationWindow = window.open(
			verificationHostedUrl.value,
			"_blank",
			`width=${width},height=${height},left=${left},top=${top}`
		)

		payInvoiceModal.value.open = false
		watchVerificationWindow(verificationWindow)
	}

	// Stripe's verification page is its own window and tells us nothing when the visitor finishes,
	// so the intent is re-checked when that window closes or when this tab is looked at again
	function watchVerificationWindow(verificationWindow: Window | null) {
		stopWatchingVerification()

		verificationWatcher = window.setInterval(() => {
			if (verificationWindow && !verificationWindow.closed) return

			stopWatchingVerification()
			refreshVerificationState()
		}, VERIFICATION_POLL_MS)

		document.addEventListener('visibilitychange', onTabVisible)
	}

	function stopWatchingVerification() {
		if (verificationWatcher) {
			window.clearInterval(verificationWatcher)
			verificationWatcher = 0
		}

		document.removeEventListener('visibilitychange', onTabVisible)
	}

	function onTabVisible() {
		if (document.visibilityState !== 'visible') return

		refreshVerificationState()
	}

	// asks Stripe whether the deposits have been confirmed, and tells the page once they have. the
	// answer comes from Stripe rather than a re-read, because FileMaker only hears from the webhook
	async function refreshVerificationState() {
		if (!stripe.value || !clientSecret.value) return

		try {
			const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

			if (isAwaitingMicrodepositVerification(paymentIntent)) return

			stopWatchingVerification()
			needsMicrodepositVerification.value = false
			verificationHostedUrl.value = ""

			// a canceled or failed verification also stops waiting, but nothing was paid, so only a
			// payment that is actually moving is reported as one
			if (isPaymentUnderWay(paymentIntent)) {
				emit('verification-completed', paymentConfirmation(paymentIntent))
			}
		}
		catch (error: any) {
			console.error('Error checking deposit verification:', error)
		}
	}

	// the Lambda refuses every unpayable invoice with one message, on purpose: it will not say which
	// half of a guess was wrong. the invoice is already on screen though, so where its own state
	// explains the refusal, say that instead of the generic line
	const unavailable = computed(() => {
		// Stripe has the payment even though FileMaker has not caught up, so this reads the same
		// as an invoice FileMaker already shows as paid
		if (props.invoice.status === PAID_STATUS || paidInStripe.value) {
			return { text: "This invoice has already been paid.", isError: false }
		}

		if (props.invoice.status === CANCELLED_STATUS) {
			return { text: "This invoice has been cancelled, so there is nothing to pay.", isError: false }
		}

		if (props.invoice.paymentStatus === PENDING_PAYMENT_STATUS) {
			return { text: "A payment for this invoice is already being processed.", isError: false }
		}

		if (Number(props.invoice.amountDue) <= 0) {
			return { text: "This invoice has nothing left to pay.", isError: false }
		}

		// nothing about the invoice explains it, so this really is a failure
		return { text: message.value || PAYMENT_ERROR_MESSAGE, isError: true }
	})

	// display only. FileMaker's amountDue already has tax added and payments taken off, and it is
	// what the payment is charged at, so these rows explain that figure rather than feed it
	const servicesTotal = computed(() => {
		return props.invoice.services.reduce((total, service) => total + Number(service.total || 0), 0)
	})

	const tax = computed(() => Number(props.invoice.tax || 0))

	const amountPaid = computed(() => Number(props.invoice.amountPaid || 0))

	const payingByCard = computed(() => {
		return selectedPaymentMethod.value === CARD_PAYMENT_METHOD
	})

	// the card fee only applies when paying by card, matching what the fee script does in FileMaker
	const amountDueWithFee = computed(() => {
		if (!payingByCard.value) {
			return props.invoice.amountDue
		}

		return props.invoice.amountDue + Number(props.invoice.ccFee || 0)
	})

	// the signed token authorizes every payment call and carries the invoice and account it was
	// issued for, so no credentials are sent from here
	function paymentPayload() {
		return {
			paymentToken: props.paymentToken
		}
	}

	// opens the payment dialog and asks the Lambda for a payment intent
	async function openDialog() {
		payInvoiceModal.value.open = true
		message.value = ""
		stripeElementsLoaded.value = false

		await startPayment()
	}

	// the Cancel and Close buttons. setting open directly skips the modal's close-modal hook, so the
	// cleanup the X button and a backdrop click get is run here too
	async function closeDialog() {
		payInvoiceModal.value.open = false

		await cancelUnconfirmedPayment()
	}

	// cancels the intent left behind by a closed dialog. a pending verification is left alone so the
	// visitor can still finish it by looking the invoice up again, and the live status is checked
	// rather than needsMicrodepositVerification, which is still false while the form is loading
	async function cancelUnconfirmedPayment() {
		try {
			if (stripe.value && clientSecret.value) {
				const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

				if (!isAwaitingMicrodepositVerification(paymentIntent)) {
					await cancelCurrentPaymentIntent()
				}
			}
		}
		catch (error: any) {
			console.error('Error canceling unconfirmed anonymous payment:', error)
		}

		// last, because the page drops the invoice in response and that unmounts this dialog
		emit('closed')
	}

	// abandons the pending verification and reloads the payment form so the visitor can pick a
	// different method
	async function switchPaymentMethod() {
		try {
			switchingPaymentMethod.value = true
			message.value = ""

			const response = await cancelCurrentPaymentIntent()

			if (response.result !== 'OK') {
				message.value = response.message || CANCEL_ERROR_MESSAGE
				return
			}

			verificationHostedUrl.value = ""
			needsMicrodepositVerification.value = false
			stripeElementsLoaded.value = false

			// FileMaker clears its own status from the payment_intent.canceled webhook, so tell the
			// page now rather than leaving a stale badge and a misleading button until the next fetch
			emit('verification-canceled')

			await startPayment()
		}
		catch (error: any) {
			console.error('Error canceling pending verification:', error)
			message.value = guestPayErrorMessage(error) || CANCEL_ERROR_MESSAGE
		}
		finally {
			switchingPaymentMethod.value = false
		}
	}

	// cancels whichever intent the dialog is currently holding
	async function cancelCurrentPaymentIntent() {
		return await cancelGuestPaymentIntent({
			...paymentPayload(),
			paymentId: currentPaymentIntentId()
		})
	}

	// the intent id is the part of the client secret before the secret itself
	function currentPaymentIntentId() {
		return clientSecret.value.split("_secret")[0]
	}

	async function startPayment() {
		try {
			loading.value = true
			paidInStripe.value = false
			paymentUnavailable.value = false

			const response = await createGuestPaymentIntent(paymentPayload())

			if (response.result === 'alreadyPaid') {
				paidInStripe.value = true
				failPayment(response.message)
				return
			}

			if (response.result !== 'OK' || !response.clientSecret) {
				failPayment(response.message)
				return
			}

			clientSecret.value = response.clientSecret

			await mountStripeElements(response.financialConnectionsClientSecret ?? "")
			stripeElementsLoaded.value = true
		}
		catch (error: any) {
			console.error('Error creating anonymous PaymentIntent:', error)
			failPayment(guestPayErrorMessage(error))
		}
		finally {
			loading.value = false
		}
	}

	// shows why the payment could not start and leaves the dialog with nothing to submit, since no
	// Payment Element was mounted
	function failPayment(serverMessage: string | undefined) {
		message.value = serverMessage || PAYMENT_ERROR_MESSAGE
		paymentUnavailable.value = true
		stripeElementsLoaded.value = true
	}

	// mounts the Stripe Payment Element. no customer session is passed, so saved payment methods
	// are never redisplayed to an anonymous visitor
	async function mountStripeElements(financialConnectionsClientSecret: string) {
		stripe.value = await stripePromise

		// an intent already parked on a verification gets the panel instead of the payment form
		const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

		if (isAwaitingMicrodepositVerification(paymentIntent)) {
			showVerificationPanel(paymentIntent)
			return
		}

		const elementsOptions = {
			clientSecret: clientSecret.value,
			financialConnectionsClientSecret: financialConnectionsClientSecret,
			appearance: {
				theme: 'flat',
				variables: {
					fontWeightNormal: '500',
					colorPrimary: '#3830a3',
					borderRadius: '6px',
					tabSpacing: '8px',
					gridColumnSpacing: '16px',
					gridRowSpacing: '24px',
				},
				rules: {
					'.Input': {
						backgroundColor: '#fff',
						border: '1px solid rgba(9,9,11,.1)'
					},
					'.CheckboxInput': {
						backgroundColor: '#fff',
						border: '1px solid rgba(9,9,11,.1)'
					},
				}
			},
		}

		const options = {
			layout: {
				type: 'tabs',
				defaultCollapsed: false,
			}
		}

		const elementsInstance = stripe.value.elements(elementsOptions)
		const paymentElement = elementsInstance.create('payment', options)
		paymentElement.mount('#anonymous-payment-element')
		elements.value = elementsInstance

		paymentElement.on('change', async function (event: any) {
			await nextTick()
			selectedPaymentMethod.value = event.value.payment_method?.type || event.value.type
		})
	}

	// settles the convenience fee against the real invoice, then confirms the payment
	async function submitPayment() {
		if (!stripe.value || !elements.value) return

		try {
			submitting.value = true
			message.value = ""

			const feeApplied = await applyConvenienceFee()

			if (!feeApplied) {
				return
			}

			const { error, paymentIntent } = await stripe.value.confirmPayment({
				elements: elements.value,
				redirect: 'if_required'
			})

			if (error) {
				message.value = error.message
				return
			}

			// a bank without instant verification lands here: the deposits are on their way, so the
			// dialog switches to the verification panel rather than reporting the payment as done
			if (isAwaitingMicrodepositVerification(paymentIntent)) {
				showVerificationPanel(paymentIntent)

				// FileMaker only learns of this from the webhook, so tell the page now rather than
				// leaving the card showing an unpaid invoice behind the closing dialog
				emit('verification-started')

				openVerificationWindow()
				return
			}

			if (isPaymentUnderWay(paymentIntent)) {
				payInvoiceModal.value.open = false
				emit('paid', paymentConfirmation(paymentIntent))
			}
		}
		catch (error: any) {
			console.error('Error confirming anonymous payment:', error)
			message.value = guestPayErrorMessage(error) || PAYMENT_ERROR_MESSAGE
		}
		finally {
			submitting.value = false
		}
	}

	// asks the Lambda to add or remove the card fee and move the intent to the new total
	async function applyConvenienceFee(): Promise<boolean> {
		const response = await applyGuestConvenienceFee({
			...paymentPayload(),
			paymentId: currentPaymentIntentId(),
			payingByCard: payingByCard.value
		})

		if (response.result !== 'OK') {
			message.value = response.message || PAYMENT_ERROR_MESSAGE
			return false
		}


		return true
	}

	onUnmounted(() => {
		stopWatchingVerification()
	})

	defineExpose({ openDialog })
</script>
